import { FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, ShieldCheck } from "lucide-react";

function usePasswordRecoveryVerification() {
  const location = useLocation();

  const [verifying, setVerifying] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const verifyToken = async () => {
      try {
        const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
        const searchParams = new URLSearchParams(location.search);

        const type = hashParams.get("type") ?? searchParams.get("type");

        if (searchParams.get("code")) {
          const code = searchParams.get("code");
          if (!code) {
            throw new Error("Link inválido ou expirado.");
          }

          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            throw error;
          }
        } else {
          const accessToken = hashParams.get("access_token");
          const refreshToken = hashParams.get("refresh_token");

          if (type !== "recovery" || !accessToken || !refreshToken) {
            throw new Error("Link inválido ou expirado.");
          }

          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            throw error;
          }
        }

        if (!isMounted) return;

        setErrorMessage(null);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Não foi possível validar o link de recuperação.";
        if (isMounted) {
          setErrorMessage(message);
        }
      } finally {
        if (isMounted) {
          setVerifying(false);
        }
      }
    };

    void verifyToken();

    return () => {
      isMounted = false;
    };
  }, [location.hash, location.search]);

  return useMemo(
    () => ({ verifying, errorMessage }),
    [verifying, errorMessage]
  );
}

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const { verifying, errorMessage } = usePasswordRecoveryVerification();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (formSuccess) {
      const timeout = setTimeout(() => {
        navigate("/admin-login");
      }, 2500);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [formSuccess, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password.length < 6) {
      setFormError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("As senhas não conferem.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);

      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        throw error;
      }

      setFormSuccess("Senha atualizada com sucesso! Redirecionando para a tela de login...");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Não foi possível atualizar a senha.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Validando link de recuperação...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-2xl font-semibold">Recuperação de senha</CardTitle>
            <CardDescription>O link que você acessou é inválido ou já foi utilizado.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTitle>Não foi possível continuar</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
            <Button className="w-full" onClick={() => navigate("/admin-login")}>Voltar para o login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl font-semibold flex items-center justify-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Definir nova senha
          </CardTitle>
          <CardDescription>Informe uma nova senha para acessar o painel administrativo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {formError && (
            <Alert variant="destructive">
              <AlertTitle>Erro ao atualizar senha</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          {formSuccess && (
            <Alert>
              <AlertTitle>Senha atualizada</AlertTitle>
              <AlertDescription>{formSuccess}</AlertDescription>
            </Alert>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="password">Nova senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={event => setPassword(event.target.value)}
                minLength={6}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repita a senha"
                value={confirmPassword}
                onChange={event => setConfirmPassword(event.target.value)}
                minLength={6}
                required
              />
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={submitting}>
              {submitting ? "Atualizando..." : "Atualizar senha"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
